import memoizeOne from 'memoize-one';
import React, { PureComponent } from 'react';
import { VirtualBrand, VirtualPack } from '~/Api/Analytics/VirtualBrand';
import { AnalyticDataSources } from '~/Api/Analytics/VirtualBrand/mapAnalyticVirtualBrand';
import Country from '~/Api/Country/Country';
import { RegionAvailability } from '~/MedicinalBrandsAnalytics/Region';
import Message from '~/MedicinalBrandsAnalytics/VirtualBrand/Message';
import RegionSelect from '~/MedicinalBrandsAnalytics/VirtualBrand/RegionSelect';
import { Reimbursement } from '~/MedicinalBrandsAnalytics/VirtualBrand/Reimbursement';
import ReimbursementInfo, { ReimbursementGroup } from '~/MedicinalBrandsAnalytics/VirtualBrand/ReimbursementInfo';
import ActivityIndicator from '~/UI/ActivityIndicator';

import styles from './styles.less';

export interface TabState {
	regionAvailability: RegionAvailability | null;
}

interface Props {
	virtualBrand: VirtualBrand;
	virtualPack: VirtualPack | null;
	virtualBrandAvailability: { [countryCode: string]: AnalyticDataSources };
	virtualPackAvailability: { [virtualPackId: string]: { [countryCode: string]: AnalyticDataSources } };
	isFetching: boolean;
	reimbursements: Reimbursement[] | null;
	defaultRegionAvailability: RegionAvailability | null;
	state: TabState | null;
	onStateChange(tabState: TabState): void;
	onLoad(virtualBrandId: string): void;
}

interface CountryReimbursement {
	country: Country;
	reimbursements: ReimbursementGroup[];
}

export default class ReimbursementTab extends PureComponent<Props> {
	componentDidMount() {
		const { virtualBrand, onLoad } = this.props;
		onLoad(virtualBrand.id);
	}

	selectReimbursements = memoizeOne(
		(reimbursements: Reimbursement[], regionAvailability: RegionAvailability): CountryReimbursement[] => {
			const groups = reimbursements.reduce((groups: { [key: string]: CountryReimbursement }, reimbursement) => {
				if (!regionAvailability.containsCountry(reimbursement.country.alpha2)) {
					return groups;
				}

				const reimbursements = reimbursement.groupVirtualPacks().map((virtualPacks) => ({
					brandName: reimbursement.brandName,
					virtualPacks,
					sentences: virtualPacks[0].sentences,
				}));

				if (groups[reimbursement.country.alpha2]) {
					Array.prototype.push.apply(groups[reimbursement.country.alpha2].reimbursements, reimbursements);
				} else {
					groups[reimbursement.country.alpha2] = {
						country: reimbursement.country,
						reimbursements,
					};
				}
				return groups;
			}, {});

			return Object.values(groups);
		}
	);

	render() {
		const {
			virtualBrandAvailability,
			virtualPack,
			isFetching,
			reimbursements,
			defaultRegionAvailability,
			state,
			onStateChange,
		} = this.props;

		if (isFetching) {
			return <ActivityIndicator />;
		}
		if (!reimbursements || !defaultRegionAvailability) {
			return null;
		}

		if (reimbursements.length === 0) {
			return <Message>{"We're sorry, but we have no reimbursement info available"}</Message>;
		}

		const regionAvailability = state?.regionAvailability || defaultRegionAvailability;
		const groupedReimbursements = this.selectReimbursements(reimbursements, regionAvailability);

		return (
			<div className={styles.content}>
				<div className={styles.leftColumn}>
					<RegionSelect
						allowIds={new Set(['europe', 'eu-eaa', 'eu-5', 'eu-cee'])}
						availability={virtualBrandAvailability}
						selectedRegionAvailability={regionAvailability}
						onSelect={(regionAvailability) => onStateChange({ regionAvailability })}
					/>
				</div>
				<div className={styles.rightColumn}>
					{virtualPack ? <Message>Selected package does not have any effect on reimbursement data.</Message> : null}

					{groupedReimbursements.map(({ country, reimbursements }) => (
						<ReimbursementInfo country={country} key={country.alpha2} reimbursements={reimbursements} />
					))}
				</div>
			</div>
		);
	}
}
